import React, { useEffect, useState } from 'react'
import { PolaroidList } from 'components/SA/Polaroid'
import ListConnector, { ConnectorProps as ListProps } from '../../connectors/List'
import styles from './UserPolaroids.module.scss'
import SALink from 'components/SALink'
import { PolaroidCompleteSkeleton } from 'components/SA/Polaroid/components/PolaroidSkeleton'
import { ARTWORK_STATUS } from 'components/SA/Polaroid/helpers'
import { useSelector } from 'react-redux'
import { getHitsTotal } from '../../selectors/algolia'

type UserPolaroidListProps = ListProps & {
  isFiltersVisible?: boolean
}

const UserPolaroidList = ({
  algoliaIndice,
  algoliaUserToken,
  artworks,
  artistName,
  facetsCount,
  isReady,
  isSet,
  isUsersArtworksAccount,
  userID,
  isFiltersVisible,
}: UserPolaroidListProps) => {
  const [isWaitingAlgolia, setIsWaitingAlgolia] = useState(true)
  const totalNumberOfResults = useSelector(getHitsTotal)

  const handleCheckIsPrintForDataLayerEvent = ({ originalStatus, hasOpenEditions }) => {
    const isSold = originalStatus === ARTWORK_STATUS.SOLD
    const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
    return (isSold || isUnavailable) && hasOpenEditions
  }

  useEffect(() => {
    setIsWaitingAlgolia(true)

    const timer = setTimeout(() => {
      if (facetsCount === 0 && !artworks.length) {
        setIsWaitingAlgolia(false)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [artworks])

  if (facetsCount > 0 && !artworks.length) {
    return (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4'>
          Sorry, there are no artworks that meet your selection criteria. Try to remove some filters
          to see more results.
        </h4>
      </div>
    )
  }

  if (isUsersArtworksAccount && !artworks.length) {
    return (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4 defaultTextCapitalize'>Your Portfolio Is Empty.</h4>
        <SALink className='defaultButton' href='/upload'>
          UPLOAD ARTWORKS NOW
        </SALink>
      </div>
    )
  }

  if (!artworks.length) {
    return isWaitingAlgolia ? (
      <div className={styles.polaroidListContainer}>
        {Array.from({ length: 6 }, (_, index) => (
          <PolaroidCompleteSkeleton key={index.toString()} />
        ))}
      </div>
    ) : (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4 defaultTextCapitalize'>
          {artistName} has not uploaded any artworks yet.
        </h4>
        <SALink className='defaultButton' href='/all'>
          BROWSE ARTWORKS
        </SALink>
      </div>
    )
  }

  return (
    <PolaroidList
      algoliaIndice={algoliaIndice}
      algoliaUserToken={algoliaUserToken}
      dataApi='algolia'
      dataList='artist-profile-all-artworks'
      dataListId='artist-profile-all-artworks'
      dataListName={`${artistName} Artworks`}
      artworks={artworks}
      isLoading={!isReady || !isSet}
      isArtistNameVisible={false}
      userID={userID}
      isUsersArtworksAccount={isUsersArtworksAccount}
      customStyle={!isFiltersVisible ? styles.customPolaroidListContainer : ''}
      onCheckIsPrintForDataLayerEvent={handleCheckIsPrintForDataLayerEvent}
      searchProviderAttributes={[
        { 'browse-container': true },
        { 'total-number-results': totalNumberOfResults },
        { 'filter-name': false },
        { 'filter-value': false },
      ]}
    />
  )
}

export default ListConnector(UserPolaroidList)
